import React, { useState, useEffect, useMemo } from 'react';
import { Calendar, Check, X, Clock, AlertCircle, Send, User, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import { API_URL } from '../config';
import SearchBox from './shared/SearchBox';

const PAGE_SIZE = 20;

const Conges = ({ authUser, employees, authToken }) => {
  // L'identifiant qui doit pointer vers employees.id (FK de conges.employee_id).
  // authUser.id = users.id  /  authUser.employee_id = employees.id (peuvent diverger après soft-delete).
  const currentEmployeeId = authUser?.employee_id || authUser?.id;

  const [conges, setConges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [formData, setFormData] = useState({
    employee_id: currentEmployeeId || '',
    date_debut: '',
    date_fin: '',
    type: 'conges_payes',
    motif: '',
    commentaire_employe: ''
  });

  // Charger les congés
  const loadConges = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/conges`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      
      if (!response.ok) throw new Error('Erreur lors du chargement des congés');
      
      const data = await response.json();
      setConges(data);
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConges();
  }, []);

  // Écouter l'événement pour ouvrir le modal depuis Planning ou Pointeuse
  useEffect(() => {
    const handleOpenModal = () => {
      setShowAddModal(true);
    };
    
    window.addEventListener('openCongesModal', handleOpenModal);
    return () => window.removeEventListener('openCongesModal', handleOpenModal);
  }, []);

  // Filtrer les congés selon le rôle
  const getVisibleConges = () => {
    if (!authUser) return [];
    
    if (['Patron', 'Super Manager'].includes(authUser.role_name)) {
      // Voir tous les congés
      return conges;
    } else if (authUser.role_name === 'Manager') {
      // Voir les congés de son service
      return conges.filter(c => c.service_name === authUser.service_name);
    } else {
      // Employés voient uniquement leurs congés
      return conges.filter(c => c.employee_id === currentEmployeeId);
    }
  };

  const visibleConges = getVisibleConges();

  // Filtres complémentaires + recherche + pagination
  const filteredConges = useMemo(() => {
    let arr = visibleConges;
    if (statusFilter) arr = arr.filter(c => c.statut === statusFilter);
    if (typeFilter) arr = arr.filter(c => c.type === typeFilter);
    if (search) {
      const q = search.toLowerCase();
      arr = arr.filter(c =>
        (c.employee_name || '').toLowerCase().includes(q) ||
        (c.motif || '').toLowerCase().includes(q) ||
        (c.service_name || '').toLowerCase().includes(q)
      );
    }
    // Trier : en_attente d'abord, puis date la plus récente
    return [...arr].sort((a, b) => {
      if (a.statut === 'en_attente' && b.statut !== 'en_attente') return -1;
      if (b.statut === 'en_attente' && a.statut !== 'en_attente') return 1;
      return new Date(b.date_debut) - new Date(a.date_debut);
    });
  }, [visibleConges, statusFilter, typeFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filteredConges.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedConges = filteredConges.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // Reset page si filtres changent
  useEffect(() => { setPage(1); }, [search, statusFilter, typeFilter]);

  // Soumettre une demande de congé
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/conges`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Erreur lors de la création de la demande');

      await loadConges();
      setShowAddModal(false);
      resetForm();
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  // Traiter une demande (accepter/refuser)
  const handleProcess = async (id, statut, commentaire = '') => {
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/conges/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          statut,
          commentaire_manager: commentaire,
          traite_par: currentEmployeeId
        })
      });

      if (!response.ok) throw new Error('Erreur lors du traitement de la demande');

      await loadConges();
      
      // Si le congé est accepté, déclencher un rechargement du planning
      if (statut === 'accepte') {
        window.dispatchEvent(new CustomEvent('reloadSchedules'));
      }
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  // Supprimer une demande en attente
  const handleDelete = async (id) => {
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/conges/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (!response.ok) throw new Error('Erreur lors de la suppression');

      await loadConges();
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      employee_id: currentEmployeeId || '',
      date_debut: '',
      date_fin: '',
      type: 'conges_payes',
      motif: '',
      commentaire_employe: ''
    });
  };

  // Calculer les statistiques
  const stats = {
    total: visibleConges.length,
    enAttente: visibleConges.filter(c => c.statut === 'en_attente').length,
    accepte: visibleConges.filter(c => c.statut === 'accepte').length,
    refuse: visibleConges.filter(c => c.statut === 'refuse').length,
    joursTotal: visibleConges
      .filter(c => c.statut === 'accepte')
      .reduce((sum, c) => sum + (c.nombre_jours || 0), 0)
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-text-primary">Gestion des Congés</h2>
          <p className="text-text-secondary mt-1 text-sm">Demandes et validation des congés</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg flex items-center justify-center space-x-2 transition shadow-md font-semibold w-full sm:w-auto"
        >
          <Send className="w-5 h-5" />
          <span>Demander un congé</span>
        </button>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 sm:p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-xs sm:text-sm">Total demandes</p>
              <p className="text-2xl sm:text-3xl font-bold mt-1 sm:mt-2">{stats.total}</p>
            </div>
            <FileText className="w-8 h-8 sm:w-10 sm:h-10 text-blue-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-4 sm:p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-xs sm:text-sm">En attente</p>
              <p className="text-2xl sm:text-3xl font-bold mt-1 sm:mt-2">{stats.enAttente}</p>
            </div>
            <Clock className="w-8 h-8 sm:w-10 sm:h-10 text-orange-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 sm:p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-xs sm:text-sm">Acceptées</p>
              <p className="text-2xl sm:text-3xl font-bold mt-1 sm:mt-2">{stats.accepte}</p>
            </div>
            <Check className="w-8 h-8 sm:w-10 sm:h-10 text-green-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-4 sm:p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-xs sm:text-sm">Refusées</p>
              <p className="text-2xl sm:text-3xl font-bold mt-1 sm:mt-2">{stats.refuse}</p>
            </div>
            <X className="w-8 h-8 sm:w-10 sm:h-10 text-red-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 sm:p-6 text-white shadow-lg col-span-2 sm:col-span-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-xs sm:text-sm">Jours validés</p>
              <p className="text-2xl sm:text-3xl font-bold mt-1 sm:mt-2">{stats.joursTotal}</p>
            </div>
            <Calendar className="w-8 h-8 sm:w-10 sm:h-10 text-purple-200" />
          </div>
        </div>
      </div>

      {/* Liste des congés */}
      <div className="bg-surface rounded-xl shadow-lg p-6">
        <h3 className="text-xl font-bold text-text-primary mb-4">Demandes de congés</h3>

        {/* Barre de filtres */}
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <div className="flex-1 min-w-0">
            <SearchBox value={search} onChange={setSearch} placeholder="Rechercher (nom, motif, service)..." size="md" />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 min-h-[40px] bg-surface border border-border-subtle rounded-lg text-sm text-text-secondary focus:outline-none focus:ring-2 focus:ring-indigo-500"
            aria-label="Filtrer par statut"
          >
            <option value="">Tous les statuts</option>
            <option value="en_attente">En attente</option>
            <option value="accepte">Accepté</option>
            <option value="refuse">Refusé</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 min-h-[40px] bg-surface border border-border-subtle rounded-lg text-sm text-text-secondary focus:outline-none focus:ring-2 focus:ring-indigo-500"
            aria-label="Filtrer par type"
          >
            <option value="">Tous types</option>
            <option value="conges_payes">Congés payés</option>
            <option value="rtt">RTT</option>
            <option value="recuperation">Récupération</option>
            <option value="conge_sans_solde">Sans solde</option>
            <option value="arret_maladie">Arrêt maladie</option>
          </select>
        </div>

        {loading && visibleConges.length === 0 ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 dark:border-blue-500 mx-auto"></div>
            <p className="text-text-muted mt-4">Chargement...</p>
          </div>
        ) : filteredConges.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-text-muted">
              {visibleConges.length === 0 ? 'Aucune demande de congé' : 'Aucun résultat avec ces filtres'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {pagedConges.map(conge => (
              <div
                key={conge.id}
                className={`border-2 rounded-lg p-4 ${
                  conge.statut === 'accepte' ? 'border-green-200 bg-green-50 dark:bg-green-950/40' :
                  conge.statut === 'refuse' ? 'border-red-200 bg-red-50 dark:bg-red-950/40' :
                  'border-orange-200 bg-orange-50 dark:bg-orange-950/40'
                }`}
              >
                <div className="flex items-start justify-between flex-col sm:flex-row gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                        {conge.employee_name?.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <p className="font-semibold text-text-primary">{conge.employee_name}</p>
                        <p className="text-sm text-text-secondary">{conge.service_name}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                      <div>
                        <p className="text-xs text-text-muted uppercase font-semibold mb-1">Période</p>
                        <p className="text-sm text-text-primary">
                          Du {new Date(conge.date_debut).toLocaleDateString('fr-FR')} 
                          <br />
                          au {new Date(conge.date_fin).toLocaleDateString('fr-FR')}
                        </p>
                        <p className="text-xs text-text-secondary mt-1">
                          {conge.nombre_jours} jour{conge.nombre_jours > 1 ? 's' : ''}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs text-text-muted uppercase font-semibold mb-1">Type</p>
                        <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full text-sm font-medium">
                          {conge.type === 'conges_payes' ? 'Congés payés' :
                           conge.type === 'conges_sans_solde' ? 'Sans solde' :
                           conge.type === 'rtt' ? 'RTT' :
                           conge.type === 'maladie' ? 'Maladie' : conge.type}
                        </span>
                      </div>

                      <div>
                        <p className="text-xs text-text-muted uppercase font-semibold mb-1">Statut</p>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          conge.statut === 'accepte' ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' :
                          conge.statut === 'refuse' ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300' :
                          'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300'
                        }`}>
                          {conge.statut === 'accepte' ? '✓ Acceptée' :
                           conge.statut === 'refuse' ? '✗ Refusée' :
                           '⏳ En attente'}
                        </span>
                      </div>
                    </div>

                    {conge.motif && (
                      <div className="mt-3">
                        <p className="text-xs text-text-muted uppercase font-semibold mb-1">Motif</p>
                        <p className="text-sm text-text-secondary">{conge.motif}</p>
                      </div>
                    )}

                    {conge.commentaire_employe && (
                      <div className="mt-2">
                        <p className="text-xs text-text-muted uppercase font-semibold mb-1">Commentaire employé</p>
                        <p className="text-sm text-text-secondary">{conge.commentaire_employe}</p>
                      </div>
                    )}

                    {conge.commentaire_manager && (
                      <div className="mt-2">
                        <p className="text-xs text-text-muted uppercase font-semibold mb-1">Commentaire manager</p>
                        <p className="text-sm text-text-secondary">{conge.commentaire_manager}</p>
                      </div>
                    )}

                    {conge.traite_le && (
                      <p className="text-xs text-text-muted mt-2">
                        Traité le {new Date(conge.traite_le).toLocaleDateString('fr-FR')}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-row sm:flex-col gap-2 w-full sm:w-auto">
                    {conge.statut === 'en_attente' && ['Patron', 'Super Manager'].includes(authUser.role_name) && (
                      <>
                        <button
                          onClick={() => {
                            const commentaire = prompt('Commentaire (optionnel) :');
                            handleProcess(conge.id, 'accepte', commentaire || '');
                          }}
                          className="bg-green-500 hover:bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center space-x-2 transition text-sm font-semibold flex-1 sm:flex-initial justify-center"
                        >
                          <Check className="w-4 h-4" />
                          <span>Accepter</span>
                        </button>
                        <button
                          onClick={() => {
                            const commentaire = prompt('Motif du refus (obligatoire) :');
                            if (commentaire) {
                              handleProcess(conge.id, 'refuse', commentaire);
                            }
                          }}
                          className="bg-red-500 hover:bg-red-600 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center space-x-2 transition text-sm font-semibold flex-1 sm:flex-initial justify-center"
                        >
                          <X className="w-4 h-4" />
                          <span>Refuser</span>
                        </button>
                      </>
                    )}

                    {conge.statut === 'en_attente' && conge.employee_id === currentEmployeeId && (
                      <button
                        onClick={() => handleDelete(conge.id)}
                        className="bg-gray-500 hover:bg-gray-600 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center space-x-2 transition text-sm font-semibold w-full sm:w-auto justify-center"
                      >
                        <X className="w-4 h-4" />
                        <span>Annuler</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {filteredConges.length > PAGE_SIZE && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-border-subtle text-sm text-text-secondary">
            <span>
              {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filteredConges.length)} sur {filteredConges.length}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={safePage <= 1}
                className="p-2 rounded-md hover:bg-elevated-1 disabled:opacity-40 disabled:cursor-not-allowed min-h-[36px] min-w-[36px] flex items-center justify-center"
                aria-label="Page précédente"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-2 font-medium">Page {safePage} / {totalPages}</span>
              <button
                type="button"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={safePage >= totalPages}
                className="p-2 rounded-md hover:bg-elevated-1 disabled:opacity-40 disabled:cursor-not-allowed min-h-[36px] min-w-[36px] flex items-center justify-center"
                aria-label="Page suivante"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal demande de congé */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-surface rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-text-primary">Demander un congé</h3>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="text-text-muted hover:text-text-secondary"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Sélection employé (Patron, Super Manager uniquement) */}
                {['Patron', 'Super Manager'].includes(authUser.role_name) && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Employé</label>
                    <select
                      value={formData.employee_id}
                      onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                      className="w-full border border-border-strong rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:border-blue-500"
                      required
                    >
                      <option value="">Sélectionner un employé</option>
                      {employees.map(emp => (
                        <option key={emp.id} value={emp.id}>
                          {emp.first_name} {emp.last_name} - {emp.role_name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Date de début</label>
                    <input
                      type="date"
                      value={formData.date_debut}
                      onChange={(e) => setFormData({ ...formData, date_debut: e.target.value })}
                      className="w-full border border-border-strong rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:border-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Date de fin</label>
                    <input
                      type="date"
                      value={formData.date_fin}
                      onChange={(e) => setFormData({ ...formData, date_fin: e.target.value })}
                      min={formData.date_debut}
                      className="w-full border border-border-strong rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:border-blue-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Type de congé</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full border border-border-strong rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:border-blue-500"
                    required
                  >
                    <option value="conges_payes">Congés payés</option>
                    <option value="conges_sans_solde">Congés sans solde</option>
                    <option value="rtt">RTT</option>
                    <option value="maladie">Maladie</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Motif</label>
                  <input
                    type="text"
                    value={formData.motif}
                    onChange={(e) => setFormData({ ...formData, motif: e.target.value })}
                    placeholder="Ex: Vacances d'été, raison personnelle..."
                    className="w-full border border-border-strong rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Commentaire (optionnel)</label>
                  <textarea
                    value={formData.commentaire_employe}
                    onChange={(e) => setFormData({ ...formData, commentaire_employe: e.target.value })}
                    placeholder="Informations supplémentaires..."
                    rows="3"
                    className="w-full border border-border-strong rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:border-blue-500"
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition disabled:opacity-50"
                  >
                    {loading ? 'Envoi en cours...' : 'Envoyer la demande'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      resetForm();
                    }}
                    className="px-6 py-3 border border-border-strong rounded-lg text-text-secondary hover:bg-elevated-1 transition font-semibold"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Conges;
